import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import StatusDropdown, { Status } from "../../components/StatusDropdown";
// dacă ai un sidebar pentru medici, folosește-l aici
import MedicSidebar from "../../components/MedicSidebar";

interface Appointment {
  id: number;
  therapist_id: string;      // id-ul medicului curent
  therapist_name?: string;
  patient_name: string;
  type: "therapy" | "activity" | "medical_activity" | "medical_consult";
  appointment_description: string; // key (code)
  status: string;
  start_time: string; // "YYYY-MM-DDTHH:mm:00"
  end_time: string;
}

interface TimeInterval {
  start: string; // "09:00"
  end: string;   // "09:30"
}

interface KV {
  key: string;
  value: string;
  short_name?: string;
  type?: string;         // pentru status (therapy|activity|...)
  activity_key?: string; // pentru status activity
}

const MedicAppointments: React.FC = () => {
  const { token, userId } = useAuth();

  const [selectedDate, setSelectedDate] = useState(formatDateToYMD(new Date()));

  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  // dropdowns (ACTIVE)
  const [appointmentStatuses, setAppointmentStatuses] = useState<Status[]>([]);
  const [activities, setActivities] = useState<KV[]>([]);
  const [therapyTypes, setTherapyTypes] = useState<KV[]>([]);
  const [medicalActivities, setMedicalActivities] = useState<KV[]>([]);
  const [consultTypes, setConsultTypes] = useState<KV[]>([]);

  // fallback maps din admin (pot include inactive)
  const [activityAdminMap, setActivityAdminMap] = useState<Record<string, { value?: string; short?: string }>>({});
  const [therapyAdminMap, setTherapyAdminMap] = useState<Record<string, { value?: string; short?: string }>>({});
  const [medActAdminMap, setMedActAdminMap] = useState<Record<string, { value?: string; short?: string }>>({});
  const [consultAdminMap, setConsultAdminMap] = useState<Record<string, { value?: string; short?: string }>>({});

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedMedic") === "true";
  });

  function formatDateToYMD(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  /* ================== LOAD META ================== */

  // time intervals
useEffect(() => {
  const fetchTimeIntervals = async () => {
    try {
      // trimitem selectedDate ca parametru către backend
      const response = await fetch(`${API_BASE_URL}/types/time-intervals?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Nu s-au putut prelua intervalele orare.");
      }
      const data = await response.json();
      setTimeIntervals(data.data || []);
    } catch (error) {
      console.error("Eroare la încărcarea intervalelor orare:", error);
    }
  };

  if (token && selectedDate) {
    fetchTimeIntervals();
  }
}, [token, selectedDate]);

  // active dropdowns
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [actRes, thRes, stRes, medActRes, consRes] = await Promise.all([
          fetch(`${API_BASE_URL}/types/activities`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/appointment-status`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/medical-activities`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/types/consult-types`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);

        const [act, th, st] = await Promise.all([actRes.json(), thRes.json(), stRes.json()]);
        setActivities(act.data || []);
        setTherapyTypes(th.data || []);
        setAppointmentStatuses(st.data || []);

        if (medActRes) {
          const d = await medActRes.json();
          setMedicalActivities(d.data || []);
        }
        if (consRes) {
          const d = await consRes.json();
          setConsultTypes(d.data || []);
        }
      } catch (e) {
        console.error("Eroare dropdowns:", e);
      }
    })();
  }, [token]);

  // admin maps (fallback pentru afișare corectă chiar dacă o cheie a devenit inactivă)
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [aAdm, tAdm, maAdm, cAdm] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/types/activities`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/admin/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/admin/types/medical-activities`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/admin/types/consult-types`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);

        if (aAdm) {
          const d = await aAdm.json();
          const map: Record<string, { value?: string; short?: string }> = {};
          (Array.isArray(d) ? d : d?.data || []).forEach((x: any) => {
            const key = x.code ?? x.key;
            map[key] = { value: x.value, short: x.short_name };
          });
          setActivityAdminMap(map);
        }
        if (tAdm) {
          const d = await tAdm.json();
          const map: Record<string, { value?: string; short?: string }> = {};
          (Array.isArray(d) ? d : d?.data || []).forEach((x: any) => {
            const key = x.code ?? x.key;
            map[key] = { value: x.value, short: x.short_name };
          });
          setTherapyAdminMap(map);
        }
        if (maAdm) {
          const d = await maAdm.json();
          const map: Record<string, { value?: string; short?: string }> = {};
          (Array.isArray(d) ? d : d?.data || []).forEach((x: any) => {
            const key = x.code ?? x.key;
            map[key] = { value: x.value, short: x.short_name };
          });
          setMedActAdminMap(map);
        }
        if (cAdm) {
          const d = await cAdm.json();
          const map: Record<string, { value?: string; short?: string }> = {};
          (Array.isArray(d) ? d : d?.data || []).forEach((x: any) => {
            const key = x.code ?? x.key;
            map[key] = { value: x.value, short: x.short_name };
          });
          setConsultAdminMap(map);
        }
      } catch (e) {
        console.warn("Admin maps fallback — unele endpointuri pot lipsi:", e);
      }
    })();
  }, [token]);

  /* ================== LOAD APPOINTMENTS (own) ================== */

  useEffect(() => {
    if (!token || !userId || !selectedDate) return;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/appointments/get-appointments/${userId}?date=${selectedDate}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        );
        if (!res.ok) throw new Error("Nu s-au putut prelua programările.");
        const data = await res.json();
        setAppointments(data.data || []);
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        console.error("Eroare la încărcarea programărilor:", e);
      }
    })();
    return () => controller.abort();
  }, [token, userId, selectedDate]);

  /* ================== STATUS CHANGE ================== */

  const handleStatusChange = async (appointmentId: number, newStatus: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/appointments/update-status/${appointmentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );
      if (!response.ok) {
        const { detail } = await response.json();
        throw new Error(detail || "Eroare la actualizarea statusului.");
      }
      setAppointments((prev) =>
        prev.map((a) => (a.id === appointmentId ? { ...a, status: newStatus } : a))
      );
    } catch (e) {
      console.error(e);
      alert("Eroare la actualizarea statusului.");
    }
  };

  /* ================== LABEL HELPERS ================== */

  const getActivityValue = (key: string) =>
    activities.find((x) => x.key === key)?.value ||
    activityAdminMap[key]?.value ||
    key;

  const getTherapyValue = (key: string) =>
    therapyTypes.find((x) => x.key === key)?.value ||
    therapyAdminMap[key]?.value ||
    key;

  const getMedActivityValue = (key: string) =>
    medicalActivities.find((x) => x.key === key)?.value ||
    medActAdminMap[key]?.value ||
    key;

  const getConsultValue = (key: string) =>
    consultTypes.find((x) => x.key === key)?.value ||
    consultAdminMap[key]?.value ||
    key;

  const getAppointmentTypeValue = (t: string) => {
    // Pentru compat: dacă ai /types/appointment-type, îl poți folosi;
    // altfel afișăm etichete simple:
    switch (t) {
      case "therapy": return "Terapie";
      case "activity": return "Activitate";
      case "medical_activity": return "Activitate medicală";
      case "medical_consult": return "Consult medical";
      default: return t;
    }
  };

  /* ================== RENDER ================== */

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <MedicSidebar onCollapseChange={(c) => setIsCollapsed(c)} />
      <div className="page-content">
        <h1 className="page-title">Programările mele</h1>

        <div className="table-top">
          <div className="page-subtitle">Programări</div>
          <div className="appointments-actions" />
          <div className="appointment-date">
            <label>Data: </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>

        <div className="table-rounded-container">
          <table className="table-rounded therapist-appointments" style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th>Interval orar</th>
                <th>Tipul programării</th>
                <th>Detalii programare</th>
                <th className="appointment-status-col">Status</th>
              </tr>
            </thead>
            <tbody>
              {timeIntervals.map((interval, rowIndex) => {
                const intervalStartISO = `${selectedDate}T${interval.start}:00`;
                const rowApps = appointments.filter(a => a.start_time === intervalStartISO);
                const cellClass = rowIndex % 2 === 0 ? "even-cell" : "odd-cell";

                if (!rowApps.length) {
                  return (
                    <tr key={interval.start}>
                      <td className={cellClass}>{`${interval.start} - ${interval.end}`}</td>
                      <td className={cellClass}>-</td>
                      <td className={cellClass}>-</td>
                      <td className={cellClass}>-</td>
                    </tr>
                  );
                }

                return rowApps.map((a, idx) => {
                  // Status-uri: pentru medical folosim setul de "therapy" (sau unul dedicat dacă ai tip separat)
                  const filteredStatuses =
                    a.type === "activity"
                      ? appointmentStatuses.filter(
                          (st) =>
                            st.type === "activity" &&
                            (st.activity_key == null || st.activity_key === a.appointment_description)
                        )
                      : appointmentStatuses.filter((st) => st.type === "therapy");

                  // Descriere
                  let details: React.ReactNode = null;
                  if (a.type === "therapy") {
                    details = (
                      <>
                        <div className="appointment-value appointment-patient">
                          <span className="appointment-text">Pacient:</span> {a.patient_name}
                        </div>
                        <div className="appointment-value appointment-type">
                          <span className="appointment-text">Terapie:</span>{" "}
                          {getTherapyValue(a.appointment_description)}
                        </div>
                      </>
                    );
                  } else if (a.type === "activity") {
                    details = (
                      <>
                        <div className="appointment-value appointment-type">
                          <span className="appointment-text">Activitate:</span>{" "}
                          {getActivityValue(a.appointment_description)}
                        </div>
                        {a.appointment_description === "evaluare" && (
                          <div className="appointment-value appointment-patient">
                            <span className="appointment-text">Pacient:</span> {a.patient_name}
                          </div>
                        )}
                      </>
                    );
                  } else if (a.type === "medical_activity") {
                    details = (
                      <div className="appointment-value appointment-type">
                        <span className="appointment-text">Activitate medicală:</span>{" "}
                        {getMedActivityValue(a.appointment_description)}
                      </div>
                    );
                  } else if (a.type === "medical_consult") {
                    details = (
                      <>
                        <div className="appointment-value appointment-type">
                          <span className="appointment-text">Consult:</span>{" "}
                          {getConsultValue(a.appointment_description)}
                        </div>
                        {/* dacă vrei și pacient la consult, îl poți afișa dacă există */}
                        {a.patient_name && (
                          <div className="appointment-value appointment-patient">
                            <span className="appointment-text">Pacient:</span> {a.patient_name}
                          </div>
                        )}
                      </>
                    );
                  }

                  return (
                    <tr key={`${interval.start}-${a.id}`}>
                      {idx === 0 && (
                        <td className={cellClass} rowSpan={rowApps.length}>
                          {`${interval.start} - ${interval.end}`}
                        </td>
                      )}
                      <td className={cellClass + " appointment-type"}>
                        {getAppointmentTypeValue(a.type)}
                      </td>
                      <td className={cellClass}>{details}</td>
                      <td className={cellClass + " appointment-status"}>
                        <StatusDropdown
                          statuses={filteredStatuses}
                          currentStatus={a.status}
                          onChange={(newStatus) => handleStatusChange(a.id, newStatus)}
                        />
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MedicAppointments;